import React from 'react';
import {
  Typography,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Box,
} from '@mui/material';
import { FONT_PRESETS, FONT_OPTIONS } from '../../../../theme';

// Flatten font options from all categories
const individualFonts = [
  ...FONT_OPTIONS.SANS_SERIF.map(f => ({ ...f, category: 'Sans-serif' })),
  ...FONT_OPTIONS.SERIF.map(f => ({ ...f, category: 'Serif' })),
  ...FONT_OPTIONS.MONOSPACE.map(f => ({ ...f, category: 'Monospace' })),
  ...FONT_OPTIONS.SYSTEM.map(f => ({ ...f, category: 'System' })),
];

const fontPresets = FONT_PRESETS;

/**
 * TypographySection Component
 *
 * Displays the typography customization section in the theme customizer.
 * Includes font family, sizes, weights, and advanced typography settings.
 *
 * @param {Object} props
 * @param {Object} props.themeData - Current theme data object
 * @param {Function} props.updateThemeData - Function to update theme data
 */
function TypographySection({ themeData, updateThemeData }) {
  return (
    <>
      <Typography variant="h6" gutterBottom>
        Font Family
      </Typography>
      <FormControl fullWidth margin="normal">
        <InputLabel>Font Family</InputLabel>
        <Select
          value={(() => {
            const currentFont = themeData.font_family || fontPresets[0].value;
            // Check if current font matches any preset
            const matchingPreset = fontPresets.find(
              preset => preset.value === currentFont
            );
            if (matchingPreset) {
              return currentFont;
            }
            // If no match, set to custom and store the custom value
            return 'custom';
          })()}
          onChange={e => {
            if (e.target.value === 'custom') {
              updateThemeData(
                'font_family',
                themeData.custom_font_family || themeData.font_family || 'Arial'
              );
            } else {
              updateThemeData('font_family', e.target.value);
            }
          }}
          label="Font Family"
        >
          {fontPresets.map(preset => (
            <MenuItem key={preset.name} value={preset.value}>
              {preset.name}
            </MenuItem>
          ))}
          <MenuItem value="custom">Custom...</MenuItem>
        </Select>
        {(() => {
          const currentFont = themeData.font_family || fontPresets[0].value;
          const matchingPreset = fontPresets.find(
            preset => preset.value === currentFont
          );
          return (
            !matchingPreset && (
              <TextField
                fullWidth
                label="Custom Font Family"
                placeholder="'Your Font', 'Fallback Font', sans-serif"
                value={themeData.font_family || ''}
                onChange={e => updateThemeData('font_family', e.target.value)}
                helperText="Enter comma-separated font families"
                margin="normal"
              />
            )
          );
        })()}
      </FormControl>

      <Divider sx={{ my: 3 }} />

      <Typography variant="h6" gutterBottom>
        Individual Font Selection
      </Typography>
      <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
        Choose a specific font for more precise control
      </Typography>
      <FormControl fullWidth margin="normal">
        <InputLabel>Select Font</InputLabel>
        <Select
          value={themeData.selected_font || ''}
          onChange={e => {
            updateThemeData('selected_font', e.target.value);
            // Update font_family with the selected font
            if (e.target.value) {
              updateThemeData('font_family', `'${e.target.value}', sans-serif`);
            }
          }}
          label="Select Font"
        >
          {individualFonts.map(font => (
            <MenuItem key={font.value} value={font.value}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  width: '100%',
                }}
              >
                <span style={{ fontFamily: font.value }}>{font.name}</span>
                <Typography
                  variant="caption"
                  color="textSecondary"
                  sx={{ ml: 1 }}
                >
                  {font.category}
                </Typography>
              </Box>
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <Divider sx={{ my: 3 }} />

      <Typography variant="h6" gutterBottom>
        Font Sizes
      </Typography>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 6 }}>
          <TextField
            label="Base Font Size"
            value={themeData.font_size || '14px'}
            onChange={e => updateThemeData('font_size', e.target.value)}
            helperText="Example: 14px, 1rem"
            margin="normal"
            fullWidth
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <TextField
            label="Heading Font Size"
            value={themeData.heading_font_size || '24px'}
            onChange={e => updateThemeData('heading_font_size', e.target.value)}
            helperText="Example: 24px, 1.5rem"
            margin="normal"
            fullWidth
          />
        </Grid>
      </Grid>

      <Divider sx={{ my: 3 }} />

      <Typography variant="h6" gutterBottom>
        Font Weights
      </Typography>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 6 }}>
          <FormControl fullWidth margin="normal">
            <InputLabel>Normal Weight</InputLabel>
            <Select
              value={themeData.font_weight_normal || '400'}
              onChange={e => {
                updateThemeData('font_weight_normal', e.target.value);
              }}
              label="Normal Weight"
            >
              <MenuItem value="300">Light (300)</MenuItem>
              <MenuItem value="400">Regular (400)</MenuItem>
              <MenuItem value="500">Medium (500)</MenuItem>
              <MenuItem value="600">Semi Bold (600)</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <FormControl fullWidth margin="normal">
            <InputLabel>Bold Weight</InputLabel>
            <Select
              value={themeData.font_weight_bold || '700'}
              onChange={e => {
                updateThemeData('font_weight_bold', e.target.value);
              }}
              label="Bold Weight"
            >
              <MenuItem value="600">Semi Bold (600)</MenuItem>
              <MenuItem value="700">Bold (700)</MenuItem>
              <MenuItem value="800">Extra Bold (800)</MenuItem>
              <MenuItem value="900">Black (900)</MenuItem>
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      <Divider sx={{ my: 3 }} />

      <Typography variant="h6" gutterBottom>
        Advanced Typography
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Fine-tune line height and letter spacing for optimal readability
      </Typography>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 6 }}>
          <TextField
            label="Line Height"
            value={themeData.line_height || '1.5'}
            onChange={e => updateThemeData('line_height', e.target.value)}
            helperText="Recommended: 1.5 for body text, 1.2 for headings"
            margin="normal"
            fullWidth
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <TextField
            label="Letter Spacing"
            value={themeData.letter_spacing || 'normal'}
            onChange={e => updateThemeData('letter_spacing', e.target.value)}
            helperText="Example: normal, 0.5px, 0.05em"
            margin="normal"
            fullWidth
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <TextField
            label="Heading Line Height"
            value={themeData.heading_line_height || '1.2'}
            onChange={e =>
              updateThemeData('heading_line_height', e.target.value)
            }
            helperText="Tighter line height for headings"
            margin="normal"
            fullWidth
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <FormControl fullWidth margin="normal">
            <InputLabel>Text Transform (Headings)</InputLabel>
            <Select
              value={themeData.heading_text_transform || 'none'}
              onChange={e =>
                updateThemeData('heading_text_transform', e.target.value)
              }
              label="Text Transform (Headings)"
            >
              <MenuItem value="none">None</MenuItem>
              <MenuItem value="uppercase">UPPERCASE</MenuItem>
              <MenuItem value="lowercase">lowercase</MenuItem>
              <MenuItem value="capitalize">Capitalize</MenuItem>
            </Select>
          </FormControl>
        </Grid>
      </Grid>
    </>
  );
}

export default TypographySection;
